import { Button, Link, Table } from "@yoast/ui-library";
import React, { Fragment } from "react";
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";
import * as styles from "./styles.module.scss";
import { messages } from "./messages";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import SubscriptionDetails from "./SubscriptionDetails";
import formatAmount from "../../../functions/currency";
import { capitalizeFirstLetter } from "shared-frontend/functions/stringHelpers";

const SubscriptionRow = ( props ) => {
	function isActive( status, includePending = true ) {
		const activeStatus = [ "active" ];
		if ( includePending ) {
			activeStatus.push( "pending-cancel" );
		}
		return activeStatus.includes( status );
	}

	function getDetailsLabel() {
		if ( props.needsAttention || props.isInactive ) {
			return props.intl.formatMessage( messages.details );
		}
		return props.intl.formatMessage( messages.nextPaymentOn );
	}

	function getManageButtons( showManageButton, onManage ) {
		if ( ! showManageButton ) {
			return <div className={ styles.styledSpace } />;
		}

		return <Button
			className={ styles.manageButton }
			onClick={ onManage }
			aria-label={ props.intl.formatMessage( messages.manage ) }
		>
			<span className={ styles.buttonText }>{ props.intl.formatMessage( messages.manage ) }</span>
			<span className={ styles.buttonIcon }><ChevronRightIcon /></span>
		</Button>;
	}

	function getNextBilling( status, endDate, nextPayment, amount ) {
		if ( status === "pending-cancel" ) {
			return <FormattedMessage { ...messages.endsDate } values={ { endDate: endDate } } />;
		}

		if ( nextPayment && amount ) {
			return nextPayment;
		}

		return null;
	}

	function getStatus( subscription ) {
		// Currently unreachable from the overview, and deliberately left in place. `subscriptionsArray` is
		// always one element now that the server pages a flat list, so the length test is always false — but
		// that is a consequence of dropping client-side grouping, not a decision about Shopify. Removing this
		// would make restoring grouping a two-file change instead of one, and reinstating it later without
		// noticing the condition would be worse. Whoever revisits grouping should decide this branch's fate
		// with it.
		if ( subscription.name.toLowerCase().includes( "shopify" ) && props.subscriptionsArray.length !== 1 ) {
			return "Managed by Shopify";
		}

		if ( ! props.needsAttention ) {
			if ( subscription.status === "pending-cancel" ) {
				return <FormattedMessage { ...messages.pendingCancellation } />;
			}

			return capitalizeFirstLetter( subscription.status );
		}

		if ( subscription.status === "on-hold" ) {
			return <FormattedMessage { ...messages.suspended } />;
		}

		if ( subscription.status === "expired" ) {
			return <FormattedMessage { ...messages.expired } />;
		}

		return <FormattedMessage { ...messages.expiresSoon } />;
	}

	/**
	 * Whether the subscription has a next billing cycle
	 *
	 * @param {object} subscription The subscription object.
	 *
	 * @returns {boolean} Whether the subscription has a next billing cycle.
	 */
	function hasNextBillingCycle( subscription ) {
		return subscription.status === "active" && ( subscription.hasNextPayment || subscription.hasEndDate );
	}

	/**
	 * Get the next payment information for a subscription.
	 *
	 * @param {object} subscription The subscription object.
	 *
	 * @returns {*} The next payment information.
	 */
	function getNextPaymentInformation( subscription ) {
		let endDate = null;
		if ( subscription.status === "pending-cancel" ) {
			endDate = <span data-testid="subscription-next-billing-date"><FormattedDate
				value={ subscription.hasEndDate && subscription.endDate }
				year="numeric"
				month="long"
				day="numeric"
			/></span>;
		}

		let nextPayment = null;
		let amount = null;
		if ( hasNextBillingCycle( subscription ) ) {
			nextPayment = <span data-testid="subscription-next-billing-date"><FormattedDate
				value={ subscription.hasNextPayment ? subscription.nextPayment : subscription.endDate }
				year="numeric"
				month="long"
				day="numeric"
			/></span>;
			amount = <FormattedNumber
				value={ formatAmount( subscription.billingAmount ) }
				currency={ subscription.billingCurrency }
				style="currency"
			/>;
		}

		return getNextBilling( subscription.status, endDate, nextPayment, amount );
	}

	function getColumnInformation() {
		if ( props.needsAttention || props.isInactive ) {
			return <SubscriptionDetails
				subscription={ props.subscriptionsArray[ 0 ] }
				showDetailsModal={ props.showDetailsModal }
			/>;
		}
		// `subscription.billingType` used to be rendered alongside this, but nothing ever set it on a
		// subscription — the row computes its own `billingType` below and renders that in its own column.
		return getNextPaymentInformation( props.subscriptionsArray[ 0 ] );
	}

	const subscription = props.subscriptionsArray[ 0 ];

	let billingType = null;
	if ( hasNextBillingCycle( subscription ) ) {
		billingType = subscription.requiresManualRenewal ? "Manual renewal" : "Automatic renewal";
	}

	const status = getStatus( subscription );

	/**
	 * Get the default columns for the subscription row.
	 *
	 * @returns {Element} The default columns for the subscription row.
	 */
	function getDefaultColumns() {
		return <>
			<Table.Cell className={ styles.tableUsage } data-label={ messages.tableHeaderUsedOn.defaultMessage }>
				{ isActive( subscription.status ) && <ul className={ styles.usageList }>
					{ subscription.isAssignableToSites && <li>
						<FormattedMessage
							{ ...messages.usedSites }
							values={ { used: subscription.used, limit: subscription.limit } }
						/>
					</li> }
					{ subscription.isInvitable && <li>
						<FormattedMessage
							{ ...messages.usedSeats }
							values={ { used: subscription.usedSeats, limit: subscription.limit } }
						/>
					</li> }
				</ul> }

			</Table.Cell>
			<Table.Cell className={ styles.tableRenewal } data-label={ messages.tableHeaderBillingType.defaultMessage }>
				<div key={ subscription.id + "-billingtype" }>
					{ billingType }
				</div>
			</Table.Cell>
			<Table.Cell className={ styles.tableBilling } data-label={ messages.tableHeaderNextBilling.defaultMessage }>
				<div
					key={ subscription.id + "-details" }
					aria-label={ getDetailsLabel() }
				>
					{ getColumnInformation() }
				</div>
			</Table.Cell>
			<Table.Cell>
				<div className={ "yst-text-right" }>
					{ ( props.needsAttention || props.isInactive ) && ! props.isProvisioned
						? getManageButtons( subscription.showManageButton, () => props.showDetailsModal( subscription ) )
						: getManageButtons( subscription.showManageButton, () => props.onManage( subscription.id ) )
					}
				</div>
			</Table.Cell>
		</>;
	}

	/**
	 * Add the columns for the partner subscriptions table to the subscription row.
	 *
	 * @returns {Element} The columns for the partner subscriptions table.
	 */
	function getPartnerColumns() {
		return (
			<>
				<Table.Cell>
					<div>
						{ /* The provisioner is resolved server-side onto the row, so rendering a partner
							subscription no longer needs the whole provisioner catalogue loaded first. Both halves
							are checked: a provisioner without a platform URL would otherwise render a link to
							nowhere. */ }
						{ subscription.provisionerName && subscription.provisionerPlatformUrl &&
							<Link href={ subscription.provisionerPlatformUrl } target="_blank">
								{ props.intl.formatMessage( messages.gotoProvisioner, { name: subscription.provisionerName } ) }
							</Link>
						}
					</div>
				</Table.Cell>
				<Table.Cell>
					<div className={ "yst-text-right" }>
						{ ( props.needsAttention || props.isInactive )
							? getManageButtons( subscription.showManageButton, () => props.showDetailsModal( subscription ) )
							: getManageButtons( subscription.showManageButton, () => props.onManage( subscription.id ) )
						}
					</div>
				</Table.Cell>
			</>
		);
	}

	return <Table.Row className={ styles.subscriptionRow }>
		<Table.Cell>
			<div className={ styles.identity }>
				<div className={ styles.icon }>
					<img src={ subscription.icon } alt="" />
				</div>
				<div className={ styles.details }>
					{ ! props.isPartOfCollapsible && subscription.name }
					<div>
						<span>
							{ status }
						</span>
						{ subscription.subscriptionNumber && <span data-testid="subscription-number">{ ` - ${subscription.subscriptionNumber}` }</span> }
					</div>
				</div>
			</div>
		</Table.Cell>
		{ props.isProvisioned ? getPartnerColumns() : getDefaultColumns() }
	</Table.Row>;
};

SubscriptionRow.propTypes = {
	onManage: PropTypes.func.isRequired,
	subscriptionsArray: PropTypes.array.isRequired,
	intl: intlShape.isRequired,
	needsAttention: PropTypes.bool,
	isProvisioned: PropTypes.bool,
	isInactive: PropTypes.bool,
	showDetailsModal: PropTypes.func,
	isPartOfCollapsible: PropTypes.bool,
};

SubscriptionRow.defaultProps = {
	showDetailsModal: null,
	isProvisioned: false,
	needsAttention: false,
	isInactive: false,
	isPartOfCollapsible: false,
};

export default injectIntl( SubscriptionRow );
